/**
 * ModuleNotAvailable — Upsell card shown when user accesses a module
 * their organization hasn't contracted.
 * Includes feature list and CTA to activate.
 */

import { Lock, ArrowRight, Sparkles, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/modules/shared/components/ui/button";
import { Card, CardContent } from "@/modules/shared/components/ui/card";
import type { ModuleKey } from "@/modules/shared/hooks/useModuleAccess";
import { useAuth } from "@/modules/shared/hooks/useAuth";

const MODULE_INFO: Record<ModuleKey, { name: string; description: string; features: string[] }> = {
  shell: { name: "Shell", description: "Core", features: [] },
  admin: { name: "Admin", description: "Administração", features: [] },
  talent: {
    name: "OpenHub Talent",
    description: "Recrutamento, ATS, gestão de pessoas, PDI, cultura e engajamento.",
    features: [
      "Talent Hub — Perfil 360° de cada profissional",
      "Smart Assessments com correção por IA",
      "Performance contínua e check-ins",
      "PDI & Feedbacks estruturados",
      "Culture Radar e engajamento",
      "Página de carreiras pública",
    ],
  },
  sales: {
    name: "OpenHub Sales",
    description: "CRM, clientes, contratos, financeiro, comissões e revenue intelligence.",
    features: [
      "CRM completo de clientes",
      "Gestão de contratos e SLA",
      "Revenue Intelligence e forecast",
      "Rate cards e calculadora comercial",
      "Visão financeira integrada",
      "Dashboards executivos",
    ],
  },
  operations: {
    name: "OpenHub Operations",
    description: "Squads, projetos, timesheet e integrações com Jira, GitHub e GitLab.",
    features: [
      "Gestão de squads e contratos",
      "Projetos e entregas",
      "Timesheet completo",
      "Integração com Jira",
      "Métricas de fluxo e SLA",
      "Portal do cliente (auto-ativado)",
    ],
  },
};

interface Props {
  module: ModuleKey;
}

export function ModuleNotAvailable({ module }: Props) {
  const info = MODULE_INFO[module];
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleActivate = () => {
    navigate(`/pricing?module=${module}`);
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-muted-foreground" />
          </div>

          <h1 className="text-2xl font-bold tracking-tight mb-2">
            {info.name} não está ativo
          </h1>

          <p className="text-muted-foreground">
            {info.description}
          </p>
        </div>

        {/* Feature list upsell card */}
        {info.features.length > 0 && (
          <Card className="mb-6 border-[var(--line)] bg-[var(--bone)]/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-4 w-4 text-[var(--ink)]" />
                <span className="text-sm font-semibold">O que você ganha com {info.name}</span>
              </div>
              <div className="grid gap-2.5">
                {info.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 text-[var(--ink)] mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button size="lg" onClick={handleActivate} className="gap-2 w-full sm:w-auto">
            Ativar Módulo
            <ArrowRight className="w-4 h-4" />
          </Button>

          <Link to="/dashboard">
            <Button variant="outline" size="lg" className="w-full sm:w-auto">
              Voltar ao dashboard
            </Button>
          </Link>
        </div>

        <p className="text-xs text-muted-foreground mt-8 text-center">
          Já contratou? Peça ao administrador da sua empresa para ativar o módulo nas configurações.
        </p>
      </div>
    </div>
  );
}
